import React from 'react';
import { Navigate } from 'react-router-dom';
import { Server } from '../../server/server';
import { BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import { HiArrowUturnDown } from "react-icons/hi2";
import AlertModal from '../modals/AlertModal';
import BadWords from '../util/badWords';
import PageEnvelope from '../elements/PageEnvelope';
import PageHeader from '../elements/PageHeader';
import ContentPanel from '../elements/ContentPanel';
import PostPanel from '../elements/PostPanel';
import MessageModal from '../modals/MessageModal';
import './SitePage.css'
import './ForumTopicPage.css'
import QuestionModal from '../modals/QuestionModal';
import { getFirstParagraph } from '../util/util';

interface ForumTopicPageState {
  loading: boolean;
  posts: any[];
  forum: any;
  topic: any;
  alert: string;
  question: string;
  navigate: string;
  sort: boolean;
  more: any;
  edit: string;
  message: string;
  loadingMore: boolean;
  loadError: boolean;
}

class ForumTopicPage extends React.Component<{}, ForumTopicPageState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      loading: false,
      posts: [],
      topic: null,
      forum: null,
      alert: '',
      question: '',
      navigate: '',
      sort: true,
      more: null,
      edit: '',
      message: '',
      loadingMore: false,
      loadError: false
    };
  }

  componentDidMount() {
    this.load();
  }

  getForumId() {
    let i1 = window.location.pathname.lastIndexOf('/');
    let i2 = window.location.pathname.lastIndexOf('/', i1-1);
    return window.location.pathname.substring(i1, i2+1);
  }

  getTopicId() {
    let i = window.location.pathname.lastIndexOf('/');
    return window.location.pathname.substring(i+1);
  }

  getCacheKey() {
    return `forum-${this.getForumId()}-${this.getTopicId()}`;
  }

  getProfilesInPosts(posts:any[]) {
    let profiles = [];
    for (let i = 0; i < posts.length; i++) {
      let post = posts[i];
      if(profiles.indexOf(post.author) == -1)
        profiles.push(post.author);
    }
    return profiles;
  }

  async load() {
    let forums = await Server.forums.getForumsCache();
    let forum = forums ? forums.find((f:any) => f.id == this.getForumId()) : null;

    let topics = await Server.forums.getTopicsCache(this.getForumId());
    let topic = topics ? topics.find((t:any) => t.id == this.getTopicId()) : null;

    if(forum && topic) {
      this.setState({forum, topic, loading: false});
      await this.loadPosts(this.state.sort);
      return;
    }

    if(!forum) {
      this.setState({loading: true});

      let response:any = await Server.forums.getForum(this.getForumId());
      if(!response.success) {
        this.setState({loading: false, loadError: true});
        return false;
      }
  
      forum = response.forum;
    }

    if(!topic) {
      let response = await Server.forums.getTopic(this.getForumId(), this.getTopicId());
      if(!response.success) {
        this.setState({loading: false, loadError: true});
        return false;
      }
  
      topic = response.topic;
      topic.title = BadWords.clean(topic.title);
    }

    this.setState({forum, topic, loading: false});

    await this.loadPosts(this.state.sort);
  }

  addNewPostsToExistingPosts(newPosts:any[], existingPosts:any[], sort:boolean) {
    let posts = [...existingPosts];

    for (let newPost of newPosts) {
      let existingIndex = posts.findIndex(post => 
        post.author === newPost.author && post.date === newPost.date
      );
      
      if (existingIndex !== -1) 
        posts[existingIndex] = newPost;
      else
        posts.push(newPost);
    }

    this.sortPosts(posts, sort);

    return posts;
  }

  async loadPosts(sort:boolean) {
    let fid = this.getForumId();
    let tid = this.getTopicId();

    let cache = await Server.forums.getPostsCache(fid, tid, sort);

    let updating = false;
    if (cache) {
      let profiles = this.getProfilesInPosts(cache.posts);
      profiles = profiles.filter((profile:any)=>Server.public.getProfile(profile) == null);
      if(profiles.length > 0) {
        this.setState({loading: true});
        await Server.public.loadProfiles(profiles);
      }
  
      this.setState({posts: cache.posts, more: cache.more});

      if(sort && cache.more) 
        return;

      updating = cache.posts.length > 0;
      let refreshTimeItem = window.sessionStorage.getItem(`ForumTopicRefresh#${fid}#${tid}`);
      if(refreshTimeItem) {
        let refreshTime = parseInt(refreshTimeItem);
        let now = new Date().getTime();
        if(now - refreshTime < 1000 * 60) {
          this.setState({loading: false});
          return;
        }
      }
    }

    let params:any = {sort};
    if(updating) {
      let pi = sort ? cache.posts.length-1 : 1;
      params.after = {date: cache.posts[pi].date, author: cache.posts[pi].author};
    }

    let response = await Server.forums.getPosts(fid, tid, params);

    if(!response.success) {
      this.setState({loading: false, alert: response.message});
      return;
    }

    window.sessionStorage.setItem(`ForumTopicRefresh#${fid}#${tid}`, new Date().getTime().toString());

    let newPosts = response.posts;
    let more = response.more;

    let profiles = this.getProfilesInPosts(newPosts);
    await Server.public.loadProfiles(profiles);

    let posts = [];
    if(updating) 
      posts = this.addNewPostsToExistingPosts(newPosts, this.state.posts, sort);
    else  
      posts = newPosts;

    await Server.forums.setPostsCache(fid, tid, sort, {posts, more}, updating);

    this.setState({posts, more, loading: false});
  }

  async loadMorePosts() {
    this.setState({loadingMore: true});

    let response = await Server.forums.getPosts(this.getForumId(), this.getTopicId(), {sort: this.state.sort, more: this.state.more});

    if(!response.success) {
      this.setState({loadingMore: false, alert: response.message});
      return;
    }

    let newPosts = response.posts;
    let more = response.more;

    let profiles = this.getProfilesInPosts(newPosts);
    await Server.public.loadProfiles(profiles);

    let posts = this.addNewPostsToExistingPosts(newPosts, this.state.posts, this.state.sort);

    await Server.forums.setPostsCache(this.getForumId(), this.getTopicId(), this.state.sort, {posts, more}, true);

    this.setState({posts, more, loadingMore: false});
  }

  sortPosts(posts:any[], sort:boolean) {
    let firstPost = posts[0];
    posts.splice(0, 1);

    posts.sort((a:any, b:any)=>{
      if(a.date > b.date)
        return sort ? 1 : -1;
      else if(a.date < b.date)
        return sort ? -1 : 1;
      return 0;
    })

    posts.unshift(firstPost);
  }

  onEditTopic() {
    this.setState({navigate: `/forum/${this.getForumId()}/${this.getTopicId()}/edit`})
  }

  async onCreatePost(content:string) {
    let now = new Date().toISOString();

    let newPost = {
      author: Server.user.getId(),
      content: content,
      date: now
    };
    
    this.state.posts.push(newPost);
    this.sortPosts(this.state.posts, this.state.sort);
    this.forceUpdate();

    let response = await Server.forums.createPost(this.getForumId(), this.getTopicId(), content);
    
    if(response.success) {
      newPost.date = response.date;
      
      await Server.forums.clearPostsCache(this.getForumId(), this.getTopicId());

      if(this.state.sort)
        this.onJumpToBottom();
    }
    else
      this.setState({alert: response.message})
  }

  onEditPost(date:string) {
    this.setState({edit: date});
  }

  async onSavePost(content:string) {
    this.setState({edit: null});

    if(content == null) 
      return;

    for(let i = 0; i < this.state.posts.length; i++) {
      if(this.state.posts[i].date == this.state.edit) {
        if(content == '')
          this.state.posts.splice(i, 1);
        else
          this.state.posts[i].content = content;
        break;
      }
    }

    let response = null;

    if(content == '') 
      response = await Server.forums.deletePost(this.getForumId(), this.getTopicId(), this.state.edit);
    else 
      response = await Server.forums.editPost(this.getForumId(), this.getTopicId(), this.state.edit, content);

    if (response.success) 
      await Server.forums.clearPostsCache(this.getForumId(), this.getTopicId());
    else 
      this.setState({alert: response.message})
  }

  onSort() {
    if(this.state.more)
      this.loadPosts(!this.state.sort);
    else 
      this.sortPosts(this.state.posts, !this.state.sort);

    this.setState({sort: !this.state.sort});
  }

  onJumpToReplies() {
    document.getElementById('RepliesHeader')?.scrollIntoView({behavior: 'smooth', block: 'center'});
  }

  onJumpToBottom() {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }

  async onLoadMore() {
    await this.loadMorePosts();
  }

  onShareTopic() {
    // console.log(this.state.posts);
    this.setState({question: 'Share this topic to the activity feed?'})
  }

  async onQuestionYes() {
    let title = this.state.topic.title;
    let author = Server.public.getProfile(this.state.topic.author).name;
    let description = getFirstParagraph(this.state.posts[0].content);
    let url = window.location.href;

    this.setState({message: 'Sharing...', question: ''});
    let raw = `<div class="ql-unfurl"><a style="text-decoration:none" class="ql-unfurl-anchor" href="${url}" target="_blank"><div class="ql-unfurl-container"><div class="ql-unfurl-text" contenteditable="false" suppresscontenteditablewarning="true"><div class="site-page-subtext">${author}</div><div><b>${title}</b></div><div>${description}</div></div></div></a></div>`
    let content = encodeURIComponent(raw);
    let response = await Server.activity.createPost(content);
    await Server.public.sleep(250);
    this.setState({message: ''});
    if(!response.success) 
      this.setState({alert: response.message});
  }

  renderRepliesHeader() {
    let count = this.state.posts.length;
    let label = (count > 2) ? `${count-1}${this.state.more ? '+' : ''} Replies` : '1 Reply';
    let headerId = 'RepliesHeader';
    
    return (
      <div className="site-page-header" id={headerId}>
        <div className="site-page-row" style={{justifyContent: 'space-between', userSelect: 'none'}}>
          <div>
            {label}
          </div>
          <div style={{display: 'flex', flexDirection: 'row', columnGap: '15px'}}>
            <div style={{cursor: 'pointer'}} onClick={()=>this.onJumpToBottom()}>
              Bottom&nbsp;<HiArrowUturnDown style={{fontSize: '0.9em', transform: 'translateY(2px)'}} />
            </div>
            <div style={{cursor: 'pointer'}} onClick={()=>this.onSort()}>
              Sort&nbsp;
              {this.state.sort ?
                <BsCaretUpFill style={{fontSize: '1.0em', transform: 'translateY(3px)'}} /> :
                <BsCaretDownFill style={{fontSize: '1.0em', transform: 'translateY(3px)'}} />
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderReplies() {
    let divs = [];

    for (let i = 1; i < this.state.posts.length; i++) {
      let post = this.state.posts[i];
      let title = (i == 0) ? this.state.topic.title : '';
      let action = post.author == Server.user.getId() && !this.state.topic.archived ? 'edit' : '';
      if(this.state.forum && this.state.forum.archived)
        action = '';

      if(this.state.edit == this.state.posts[i].date) {
        divs.push(
          <PostPanel 
            key={i}
            placeholder="Enter reply..."
            edit={this.state.posts[i].content}
            onPost={(content:string)=>this.onSavePost(content)}
            maxLength={1024}
          />
        )
      }
      else {
        divs.push(
          <ContentPanel 
            key={i}
            author={post.author}
            date={post.date}
            content={post.content}
            title={title}
            reply={true}
            action={action}
            onAction={()=>this.onEditPost(post.date)}
          />
        );
      }
    }

    return divs;
  }

  renderLoading() {
    let message = this.state.loadError ? 'Forum topic not found' : 'Loading...';
    return (
      <div className="site-page">
        <PageEnvelope width="600px">
          <PageHeader text={message} navigate="/forums" />
        </PageEnvelope>
      </div>
    )
  }

  render() {
    if(this.state.navigate != '') 
      return <Navigate to={this.state.navigate} state={{from:'forum-topic'}} />;

    // if(!Server.account.isLoggedIn())
    //   return <Navigate to="/forums" />

    if(this.state.loading || !this.state.topic || this.state.posts.length == 0)
      return this.renderLoading();

    let action = this.state.topic.author == Server.user.getId() ? 'edit' : '';
    if(this.state.forum && this.state.forum.archived)
      action = '';

    let blockReply = (this.state.topic && this.state.topic.archived) || (this.state.forum && this.state.forum.archived);
    let firstPost = this.state.posts[0];

    return (
      <div className="site-page">
        <PageEnvelope width="600px">
          <div className="site-page-column">

            <PageHeader text="View Topic" navigate={'/forum/' + this.getForumId()} force={true} onBack={()=>this.setState({navigate: '/forum/' + this.getForumId()})}>
              {this.state.posts.length > 1 &&
                <div style={{display: 'flex', cursor: 'pointer'}} onClick={()=>this.onJumpToReplies()}>
                  Replies&nbsp;<HiArrowUturnDown style={{fontSize: '0.9em', transform: 'translateY(4px)'}} />
                </div>
              }
            </PageHeader>

            {this.state.forum && this.state.forum.archived && 
              <div className="site-page-header" style={{justifyContent: 'center'}}>
                This forum has been archived.
              </div>
            }

            {this.state.topic && this.state.topic.archived && 
              <div className="site-page-header" style={{justifyContent: 'center'}}>
                This topic has been archived.
              </div>
            }

            {firstPost &&
              <ContentPanel 
                author={firstPost.author}
                date={firstPost.date}
                content={firstPost.content}
                title={this.state.topic.title}
                action={action}
                onAction={()=>this.onEditTopic()}
                onShare={()=>this.onShareTopic()}
              />
            }

            {!blockReply && 
              <PostPanel 
                placeholder="Enter reply..."
                onPost={(content:string)=>this.onCreatePost(content)}
                maxLength={1024}
              />
            }

            {this.state.posts.length > 1 &&
              this.renderRepliesHeader()
            }

            {!this.state.loading && this.state.posts.length > 1 &&
              <div className="site-page-column">
                {this.renderReplies()}
              </div>
            }

            {this.state.more &&
              <button onClick={()=>this.onLoadMore()} style={{backgroundColor: 'var(--page-background-color)', border: '1px solid var(--panel-background-color)'}}>
                {this.state.loadingMore ? 'Loading...' : 'Load More Replies'}
              </button>
            }

            {!blockReply && this.state.posts.length > 3 &&
              <PostPanel 
                placeholder="Enter reply..."
                onPost={(content:string)=>this.onCreatePost(content)}
                maxLength={1024}
              />
            }
          </div>
        </PageEnvelope>

        <MessageModal message={this.state.message} />
        <AlertModal message={this.state.alert} button="OK" onClose={()=>this.setState({alert: ''})}/>
        <QuestionModal message={this.state.question} onYes={()=>this.onQuestionYes()} onNo={()=>this.setState({question: ''})} />
      </div>
    );
  }
}

export default ForumTopicPage;